import { useEffect, useRef, useState } from 'react';
import { prepareSound, sfx, say, stopSound, resumeVoice, setMuted } from './sound.js';
import { WA_PATH, WHATSAPP } from './brand.jsx';
import { TESTIMONIALS, thumb } from './testimonials.js';

// Hero animation: one day of FBZipper on autopilot. Posts are uploaded to the
// dashboard, the profile joins new groups from the database, posts go out to
// every group (and show up in people's feeds), commenters get tagged and
// answered and become leads, Messenger is answered, Marketplace listings go
// live, then the price card. One timeline (ms since start) drives every frame.
//
// Each step lasts as long as its voice line (public/sound/*/stepN.mp3, from
// scripts/voice.mjs) plus a beat; events inside a step are offsets from its start.
export const STEP_LEN = [8000, 10400, 14000, 9800, 4500, 5500, 9800, 21000];
export const STEP_AT = STEP_LEN.reduce((a, len, i) => [...a, i ? a[i - 1] + STEP_LEN[i - 1] : 0], []);
const END = STEP_AT[7] + STEP_LEN[7];
const OUTRO = STEP_AT[7];

// Offsets within each step (ms).
const UP = { tile: 500, tileGap: 900, upload: 1100, chips: 3600, chipGap: 600, autopilot: 5600 };
const JOIN = { rows: 300, rowGap: 250, joinAt: [2600, 4200, 5800] };
const POST = { cards: 500, card: 1100, publish: 0.4, feed: 7300, feedSpeed: 0.075 };
const COMMENT = { first: 700, gap: 2600, reply: 1000, lead: 1500 };
const MSG = { in: 300, typing: 900, reply: 1900 };
const MARKET = { first: 300, gap: 400, live: 600, buyer: 3600 };
const REVIEWS = { first: 300, gap: 220, stat1: 2200, stat2: 3600, cta: 5400 };

const JOIN_ROWS = [1, 3, 5]; // groups the profile wasn't in yet
const FROM = ['profile', 'page', 'profile', 'page', 'profile', 'page']; // who posts in each group
const COLORS = ['#FFD6A5', '#CDE7FF', '#E4D4FF', '#B9E4C9', '#FFC9D6', '#FFF1A8'];
const GROUP_BG = ['linear-gradient(135deg,#FF9A3C,#FF4E50)', 'linear-gradient(135deg,#4FACFE,#0866FF)', 'linear-gradient(135deg,#A18CD1,#7C5CFF)', 'linear-gradient(135deg,#43E97B,#11A36B)', 'linear-gradient(135deg,#FA709A,#E0457B)', 'linear-gradient(135deg,#F6D365,#FDA085)'];
const POST_BG = ['linear-gradient(135deg,#FFE6C7,#FFC78A)', 'linear-gradient(135deg,#D8ECFF,#9CCBFF)', 'linear-gradient(135deg,#E6F7EC,#A6E3BE)'];
const clamp01 = (x) => Math.max(0, Math.min(1, x));
const ease = (x) => 1 - Math.pow(1 - clamp01(x), 3);
const first = (s) => [...s][0];

function clock(ms) {
  // 07:00 at the start to ~22:40 at the price card.
  const m = Math.round(420 + (Math.min(ms, OUTRO) / OUTRO) * 940);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

// A Facebook group post, as it appears in the group or in someone's feed.
function GroupPost({ d, i, group, members, author, fromPage, state, likes, comments, feed }) {
  const post = d.upload.posts[i % d.upload.posts.length];
  return (
    <article className={`gpost${feed ? ' in-feed' : ''}`}>
      {!feed && (
        <div className="gpost-cover" style={{ background: GROUP_BG[i % GROUP_BG.length] }}>
          <b>{group}</b>
          <small>{d.db.public} · <bdi>{members}</bdi> {d.db.members}</small>
        </div>
      )}
      <div className="gpost-head">
        <span className={fromPage ? 'gpost-av page' : 'gpost-av'} style={{ background: fromPage ? GROUP_BG[3] : COLORS[0] }}>
          {fromPage ? '🏢' : first(author)}
        </span>
        <span className="gpost-who">
          <b>
            {author}
            {feed && <span className="gpost-to"> ▸ {group}</span>}
          </b>
          <small>{d.post.when} · 🌐</small>
        </span>
        <span className="gpost-more">···</span>
      </div>
      <p className="gpost-text">{post.name} · {post.meta}</p>
      <div className="gpost-img" style={{ background: POST_BG[i % POST_BG.length] }}>
        <span>{post.e}</span>
      </div>
      <div className="gpost-stats">
        <span><i>👍</i><i>❤️</i> <bdi>{likes}</bdi></span>
        <span><bdi>{comments}</bdi> 💬</span>
      </div>
      <div className="gpost-actions">
        <span>👍 {d.actions[0]}</span>
        <span>💬 {d.actions[1]}</span>
        <span>↗ {d.actions[2]}</span>
      </div>
      {state === 'publishing' && <div className="gpost-publishing"><i />{d.db.publishing}</div>}
      {state === 'posted' && <span className="gpost-stamp">✓ {d.db.posted}</span>}
    </article>
  );
}

export default function Demo({ t, onTrial, recording }) {
  const d = t.demo;
  const root = useRef(null);
  // ?demo_t=4500 freezes the animation at that moment (for screenshots).
  const frozen = (() => {
    try {
      const v = new URLSearchParams(window.location.search).get('demo_t');
      return v === null ? null : Math.max(0, Math.min(END - 1, Number(v) || 0));
    } catch {
      return null;
    }
  })();
  // The animation waits for the play button, so it always starts with sound.
  const POSTER = STEP_AT[2] + 3400; // a post going up in a group, shown behind the play button
  const [phase, setPhase] = useState(frozen !== null ? 'frozen' : recording ? 'ready' : 'idle'); // idle | ready | playing | paused | ended | frozen
  const [soundOn, setSoundOn] = useState(true);
  const [ms, setMs] = useState(frozen ?? POSTER);
  // The step whose voice line was last started, so resuming continues the line instead of
  // restarting it.
  const said = useRef(0);
  const msRef = useRef(ms);
  msRef.current = ms;
  function play() {
    said.current = 0;
    prepareSound(STEP_AT.length);
    setMuted(!soundOn);
    setMs(0);
    setPhase('playing');
  }
  // Video export (?export=1): the recorder calls window.__fbzStart() and reads the timeline.
  useEffect(() => {
    if (!recording) return;
    window.__fbzTimeline = { STEP_AT, STEP_LEN, END };
    window.__fbzStart = () => {
      setMs(0);
      setPhase('playing');
      return Date.now();
    };
  }, [recording]);
  function toggleSound() {
    setMuted(soundOn);
    setSoundOn(!soundOn);
  }

  useEffect(() => {
    if (phase !== 'playing') return;
    let raf = 0;
    let last = performance.now();
    let elapsed = msRef.current; // 0 on play/replay, where it stopped on resume
    let visible = true;
    const io = new IntersectionObserver(([e]) => (visible = e.isIntersecting), { threshold: 0.2 });
    if (root.current) io.observe(root.current);
    const frame = (now) => {
      const dt = Math.min(100, now - last);
      last = now;
      if (visible && !document.hidden) {
        elapsed += dt;
        if (elapsed >= END) {
          setMs(END - 1);
          setPhase('ended');
          return;
        }
        setMs(Math.round(elapsed / 40) * 40); // 25 updates a second: smooth enough for the feed scroll
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      stopSound();
    };
  }, [phase]);

  const step = STEP_AT.filter((s0) => ms >= s0).length; // 1..8
  const rel = ms - STEP_AT[step - 1]; // ms into the current step
  const at = (n) => ms - STEP_AT[n - 1]; // ms into step n (negative before it)
  const outro = step === 8;
  const fading = ms >= END - 400;
  const total = 142;

  // 1. upload to the dashboard
  const tilesIn = Math.max(0, Math.min(3, Math.floor((at(1) - UP.tile) / UP.tileGap) + 1));
  const tileProgress = (i) => clamp01((at(1) - UP.tile - i * UP.tileGap - 200) / UP.upload);
  const chipsIn = Math.max(0, Math.min(3, Math.floor((at(1) - UP.chips) / UP.chipGap) + 1));
  const autopilot = step > 1 || at(1) >= UP.autopilot;
  // 2. join groups
  const rowsIn = Math.max(0, Math.min(d.groups.length, Math.floor((at(2) - JOIN.rows) / JOIN.rowGap) + 1));
  const joinedRows = JOIN.joinAt.filter((a) => at(2) >= a).length;
  // 3. posting: a card per group, then the feed
  const inFeed = step === 3 && rel >= POST.feed;
  const cardIdx = Math.max(0, Math.min(d.groups.length - 1, Math.floor((at(3) - POST.cards) / POST.card)));
  const cardT = ((at(3) - POST.cards) % POST.card) / POST.card;
  const cardState = at(3) < POST.cards ? 'publishing' : cardIdx === d.groups.length - 1 && at(3) > POST.cards + POST.card * d.groups.length ? 'posted' : cardT < POST.publish ? 'publishing' : 'posted';
  const groupN = Math.max(1, Math.round(total * ease(at(3) / STEP_LEN[2])));
  const limitPct = Math.round(82 * ease(at(3) / STEP_LEN[2]));
  const feedY = inFeed ? (rel - POST.feed) * POST.feedSpeed : 0;
  // 4. comments → tag + reply → lead
  const cAt = (i) => COMMENT.first + i * COMMENT.gap;
  const commentsIn = [0, 1, 2].filter((i) => at(4) >= cAt(i)).length;
  const repliesIn = [0, 1, 2].filter((i) => at(4) >= cAt(i) + COMMENT.reply).length;
  const commentLeads = [0, 1, 2].filter((i) => at(4) >= cAt(i) + COMMENT.lead).length;
  // 5. Messenger
  const msgLead = at(5) >= MSG.reply + 300 ? 1 : 0;
  // 6. Marketplace
  const itemsIn = Math.max(0, Math.min(d.market.items.length, Math.floor((at(6) - MARKET.first) / MARKET.gap) + 1));
  const itemsLive = Math.max(0, Math.min(d.market.items.length, Math.floor((at(6) - MARKET.first - MARKET.live) / MARKET.gap) + 1));
  const buyerLead = at(6) >= MARKET.buyer ? 1 : 0;
  // 7. video testimonials
  const REVIEW_TILES = TESTIMONIALS.slice(1, 10);
  const tilesShown = Math.max(0, Math.min(REVIEW_TILES.length, Math.floor((at(7) - REVIEWS.first) / REVIEWS.gap) + 1));

  // Live counters in the top bar. What's on screen is a sample; the counters
  // include the rest of the day's groups.
  const joined = Math.round(27 * ease(at(2) / STEP_LEN[1]));
  const posts = Math.round(186 * ease(at(3) / STEP_LEN[2])) + (at(4) > 0 ? Math.min(40, Math.floor(at(4) / 500)) : 0);
  const shownLeads = commentLeads + msgLead + buyerLead;
  const leads = shownLeads + Math.round(32 * ease(at(4) / (OUTRO - STEP_AT[3])));
  const toast =
    step === 4 && commentLeads > 0 ? { name: d.post.comments[commentLeads - 1][0], where: `${d.leads.from}"${d.post.group}"`, key: `c${commentLeads}` }
    : step === 5 && msgLead ? { name: d.messenger.who, where: d.messenger.title, key: 'm' }
    : null;

  const playing = phase === 'playing';
  // Sound effects key off `running`, which doesn't flip on pause/resume, so resuming
  // doesn't replay the current step's cues.
  const running = playing || phase === 'paused';
  // Click the stage (or the pause button) to pause and resume, like a video.
  const togglePause = () => setPhase((p) => (p === 'playing' ? 'paused' : p === 'paused' ? 'playing' : p));
  const onStageClick = (e) => {
    if (e.target.closest('button, a')) return;
    togglePause();
  };
  // Sound cues fire on a change of the listed value only, so the deps are deliberately partial.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!playing) return;
    if (said.current !== step) {
      said.current = step;
      say(step);
    } else {
      resumeVoice();
    }
  }, [step, playing]);
  useEffect(() => { if (running && step > 1 && !outro) sfx('whoosh'); }, [step, running]);
  useEffect(() => { if (running && inFeed) sfx('whoosh'); }, [inFeed, running]);
  useEffect(() => { if (running && tilesIn > 0) sfx('pop'); }, [tilesIn, running]);
  useEffect(() => { if (running && autopilot && step === 1) sfx('click'); }, [autopilot, running]);
  useEffect(() => { if (running && joinedRows > 0) sfx('click'); }, [joinedRows, running]);
  useEffect(() => { if (running && step === 3 && !inFeed && cardState === 'posted') sfx('pop'); }, [cardIdx, cardState, running]);
  useEffect(() => { if (running && repliesIn > 0) sfx('pop'); }, [repliesIn, running]);
  useEffect(() => { if (running && itemsLive > 0) sfx('pop'); }, [itemsLive, running]);
  useEffect(() => { if (running && step === 7 && tilesShown > 0) sfx('pop'); }, [tilesShown, running]);
  useEffect(() => { if (running && shownLeads > 0) sfx('ding'); }, [shownLeads, running]);
  useEffect(() => { if (running && outro) sfx('chime'); }, [outro, running]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const scene = ['upload', 'groups', inFeed ? 'feed' : 'posting', 'comments', 'messenger', 'market', 'reviews', 'outro'][step - 1];
  const on = (name) => (scene === name ? `scene s-${name} on` : `scene s-${name}`);
  const commentShift = [0, 0, 30, 96][commentsIn];
  const author = (k) => (FROM[k] === 'page' ? d.pageName : d.post.author);
  const feedItems = [0, 1, 2, 3, 4, 5, 0, 1, 2, 3];

  return (
    <div className="demo-wrap" ref={root}>
      <div
        className={`demo zstage ${phase}${fading && phase === 'playing' ? ' fading' : ''}`}
        aria-label={d.label}
        data-step={step}
        onClick={running && !recording ? onStageClick : undefined}
      >
        {/* Autopilot bar: the day's clock and running totals */}
        <div className="zbar" aria-hidden="true">
          <span className="zbar-live">
            <i />
            {d.bar.live}
          </span>
          <span className="zbar-clock" dir="ltr">{clock(ms)}</span>
          <span className="zbar-stats">
            <span><b dir="ltr">{posts}</b> {d.bar.posts}</span>
            <span><b dir="ltr">{joined}</b> {d.bar.joined}</span>
            <span className="lead-stat" key={shownLeads}><b dir="ltr">{leads}</b> {d.bar.leads}</span>
          </span>
        </div>

        {/* 1. upload posts to the dashboard */}
        <div className={on('upload')} aria-hidden="true">
          <div className="z-window">
            <div className="z-window-bar">
              <i /><i /><i />
              <span dir="ltr">🔒 {d.upload.url}</span>
            </div>
            <div className="z-window-body">
              <div className="z-connected">
                <span className="z-connected-av">{first(d.post.author)}</span>
                <b>{d.post.author}</b>
                <span className="z-connected-ok">✓ {d.upload.connected}</span>
              </div>
              <div className="z-up-head">
                <b>{d.upload.title}</b>
                <span className="z-up-btn">{d.upload.button}</span>
              </div>
              <ul className="z-tiles">
                {d.upload.posts.map((p0, i) => {
                  const pr = tileProgress(i);
                  return (
                    <li key={p0.name} className={i < tilesIn ? 'in' : ''}>
                      <span className="z-tile-img" style={{ background: POST_BG[i] }}>{p0.e}</span>
                      <b>{p0.name}</b>
                      <small>{p0.meta}</small>
                      {pr < 1 ? (
                        <span className="z-progress"><i style={{ inlineSize: `${Math.round(pr * 100)}%` }} /></span>
                      ) : (
                        <span className="z-ready">✓ {d.upload.ready}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="z-chips">
                {d.upload.chips.map((c, i) => <span key={c} className={i < chipsIn ? 'in' : ''}>{c}</span>)}
              </div>
              <div className={autopilot ? 'z-autopilot on' : 'z-autopilot'}>
                <span className={autopilot ? 'z-toggle on' : 'z-toggle'}><i /></span>
                <b>{d.upload.autopilot}</b>
                <span className="z-autopilot-state">{autopilot ? d.bar.live : ''}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 2. groups database: auto-join */}
        <div className={on('groups')} aria-hidden="true">
          <div className="z-head">
            <b>🗂 {d.db.title}</b>
            <span>{d.db.count}</span>
          </div>
          <div className="z-filters">
            {d.db.filters.map((f) => <span key={f}>{f}</span>)}
          </div>
          <ul className="z-groups">
            {d.groups.map(([name, members], i) => {
              const needsJoin = JOIN_ROWS.includes(i);
              const joinedHere = needsJoin && JOIN_ROWS.indexOf(i) < joinedRows;
              return (
                <li key={name} className={i < rowsIn ? 'in' : ''}>
                  <span className="z-gav" style={{ background: GROUP_BG[i] }}>{first(name)}</span>
                  <span className="z-gname">
                    <b>{name}</b>
                    <small><bdi>{members}</bdi> {d.db.members}</small>
                  </span>
                  {needsJoin && (
                    <span className={joinedHere ? 'z-status joined' : 'z-join'} key={joinedHere ? 'y' : 'n'}>
                      {joinedHere ? `✓ ${d.db.joined}` : `+ ${d.db.join}`}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* 3a. posting into each Facebook group */}
        <div className={on('posting')} aria-hidden="true">
          <div className="z-post-head">
            <b>📣 {d.db.posting}</b>
            <span dir="auto">{d.db.groupN(groupN, total)}</span>
          </div>
          <div className="z-progress wide"><i style={{ inlineSize: `${Math.round((groupN / total) * 100)}%` }} /></div>
          <div className="gstack">
            <span className="gghost g2" />
            <span className="gghost g1" />
            <div className="gcard" key={cardIdx}>
              <GroupPost
                d={d} i={cardIdx} group={d.groups[cardIdx][0]} members={d.groups[cardIdx][1]}
                author={author(cardIdx)} fromPage={FROM[cardIdx] === 'page'} state={cardState}
                likes={0} comments={0}
              />
            </div>
          </div>
          <div className="z-limit">
            <span className="z-limit-label">🛡️ {d.db.limit}</span>
            <span className="z-limit-bar"><i style={{ inlineSize: `${limitPct}%` }} /></span>
            <span className="z-limit-ok">{d.db.limitOk}</span>
          </div>
          <div className="z-cloud">☁️ {d.db.cloud}</div>
        </div>

        {/* 3b. the posts showing up in people's feeds */}
        <div className={on('feed')} aria-hidden="true">
          <div className="fb-top">
            <span className="fb-logo">f</span>
            <span className="fb-search">🔍 {d.db.search}</span>
            <span className="fb-icons">🏠 👥 🔔</span>
          </div>
          <div className="feed-title">✨ {d.db.feedTitle}</div>
          <div className="feed-scroll">
            <div className="feed-inner" style={{ transform: `translateY(${-feedY}px)` }}>
              {feedItems.map((k, n) => (
                <GroupPost
                  key={n} d={d} i={k} group={d.groups[k][0]} members={d.groups[k][1]}
                  author={author(k)} fromPage={FROM[k] === 'page'} feed
                  likes={Math.max(0, Math.round((n * 37 + 24) * ease((rel - POST.feed) / 4000)))}
                  comments={Math.max(0, Math.round((n * 5 + 6) * ease((rel - POST.feed) / 4000)))}
                />
              ))}
            </div>
          </div>
        </div>

        {/* 4. a posted group post: commenters get tagged and answered */}
        <div className={on('comments')} aria-hidden="true">
          <div className="z-post fb" style={{ transform: `translateY(${-commentShift}px)` }}>
            <div className="fb-head">
              <span className="z-gav" style={{ background: GROUP_BG[0] }}>{first(d.post.group)}</span>
              <div className="fb-who">
                <b>{d.post.group}</b>
                <span>{d.post.author} · {d.post.when}</span>
              </div>
            </div>
            <p className="fb-text">{d.post.text}</p>
            <ul className="z-comments">
              {d.post.comments.map(([name, text], i) => (
                <li key={name} className={i < commentsIn ? 'in' : ''}>
                  <div className="z-c">
                    <span className="av sm" style={{ background: COLORS[i + 1] }}>{first(name)}</span>
                    <span className="c-bubble"><b>{name}</b>{text}</span>
                  </div>
                  <div className={i < repliesIn ? 'z-c z-reply in' : 'z-c z-reply'}>
                    <span className="av sm" style={{ background: COLORS[0] }}>{first(d.post.author)}</span>
                    <span className="c-bubble">
                      <b>{d.post.author} <em>{d.post.autoTag}</em></b>
                      <span><span className="mention">@{name}</span> {d.post.reply}</span>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* 5. Messenger */}
        <div className={on('messenger')} aria-hidden="true">
          <div className="z-chat">
            <div className="z-chat-head">
              <span className="av" style={{ background: COLORS[3] }}>{first(d.messenger.who)}</span>
              <span>
                <b>{d.messenger.who}</b>
                <small>{d.messenger.active}</small>
              </span>
              <span className="z-chat-logo">💬 {d.messenger.title}</span>
            </div>
            <div className="z-chat-body">
              <span className={at(5) >= MSG.in ? 'bubble in them' : 'bubble them'}>{d.messenger.msg}</span>
              {at(5) >= MSG.typing && at(5) < MSG.reply && (
                <span className="bubble me typing"><i /><i /><i /></span>
              )}
              <span className={at(5) >= MSG.reply ? 'bubble in me' : 'bubble me'}>{d.messenger.reply}</span>
              <small className={at(5) >= MSG.reply + 200 ? 'z-auto in' : 'z-auto'}>⚡ {d.messenger.auto}</small>
            </div>
          </div>
        </div>

        {/* 6. Marketplace */}
        <div className={on('market')} aria-hidden="true">
          <div className="z-head">
            <b>🛍️ {d.market.title}</b>
            <span>{d.market.sub}</span>
          </div>
          <ul className="z-items">
            {d.market.items.map((it, i) => (
              <li key={it.t} className={i < itemsIn ? 'in' : ''}>
                <span className="z-item-img" style={{ background: it.bg }}>{it.e}</span>
                <b dir="ltr">{it.p}</b>
                <small>{it.t}</small>
                <span className={i < itemsLive ? 'z-live on' : 'z-live'}>● {d.market.live}</span>
              </li>
            ))}
          </ul>
          <div className={buyerLead ? 'z-buyer in' : 'z-buyer'}>💬 {d.market.buyer}</div>
        </div>

        {/* 7. hundreds of video testimonials */}
        <div className={on('reviews')} aria-hidden="true">
          <div className="z-head">
            <b>⭐ {d.reviews.title}</b>
            <span className="z-stars">★★★★★</span>
          </div>
          <ul className="z-reviews">
            {REVIEW_TILES.map((v, i) => (
              <li key={v.id} className={i < tilesShown ? 'in' : ''}>
                <img src={thumb(v.id)} alt="" loading="lazy" />
                <span className="z-play">▶</span>
              </li>
            ))}
          </ul>
          <div className="z-review-stats">
            <span className={at(7) >= REVIEWS.stat1 ? 'in' : ''}><b>{d.reviews.big1}</b>{d.reviews.sub1}</span>
            <span className={at(7) >= REVIEWS.stat2 ? 'in' : ''}><b>{d.reviews.big2}</b>{d.reviews.sub2}</span>
          </div>
          <div className={at(7) >= REVIEWS.cta ? 'z-behind in' : 'z-behind'}>🚀 {d.reviews.cta}</div>
        </div>

        {/* A new lead lands */}
        <div className="z-toasts" aria-hidden="true">
          {toast && (
            <div className="z-toast" key={toast.key}>
              <span className="z-toast-icon">🎯</span>
              <span>
                <small>{d.leads.newLead}</small>
                <b>{toast.name}</b>
                <span>{toast.where}</span>
              </span>
            </div>
          )}
        </div>

        {/* 7. price card */}
        <div className={outro ? 'demo-outro in' : 'demo-outro'} aria-hidden={!outro}>
          <span className="lbadge lsmall">☁️ {t.hero.badge}</span>
          {/* The video sells the free trial: no price, no payment. */}
          <div className="demo-outro-price">
            <strong>{d.outro.trialBig}</strong>
            <span>{d.outro.trialSub}</span>
          </div>
          <span className="demo-outro-after">{d.outro.after}</span>
          <span className="demo-outro-nudge">{d.outro.nudge}</span>
          <ul>
            {d.outro.points.map((p0) => (
              <li key={p0}>{p0}</li>
            ))}
          </ul>
          {!recording && (
            <div className="demo-outro-ctas">
              <button type="button" className="lbtn flash" onClick={onTrial} tabIndex={outro ? 0 : -1}>
                {d.outro.cta}
              </button>
              <a
                className="lbtn wa-btn"
                href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(t.whatsapp.message)}`}
                target="_blank"
                rel="noopener noreferrer"
               
                tabIndex={outro ? 0 : -1}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d={WA_PATH} /></svg>
                {d.outro.wa}
              </a>
            </div>
          )}
        </div>

        {phase === 'idle' && (
          <button type="button" className="demo-play" onClick={play}>
            <span className="demo-play-btn" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M8 5.5v13l11-6.5z" /></svg>
            </span>
            <b>{d.play}</b>
            <small>{d.playSub}</small>
          </button>
        )}
        {phase === 'ended' && !recording && (
          <button type="button" className="demo-replay" onClick={play}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5V2L7 6l5 4V7a5 5 0 1 1-5 5H5a7 7 0 1 0 7-7z" /></svg>
            {d.replay}
          </button>
        )}
        {phase === 'paused' && (
          <button type="button" className="demo-paused" onClick={togglePause} aria-label={d.resume}>
            <span className="demo-play-btn" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M8 5.5v13l11-6.5z" /></svg>
            </span>
          </button>
        )}
        {running && !recording && (
          <button type="button" className="demo-pause" onClick={togglePause} aria-label={playing ? d.pause : d.resume} title={playing ? d.pause : d.resume}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              {playing ? <path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" /> : <path d="M8 5.5v13l11-6.5z" />}
            </svg>
          </button>
        )}
        {running && !recording && (
          <button type="button" className="demo-mute" onClick={toggleSound} aria-label={soundOn ? d.mute : d.unmute} title={soundOn ? d.mute : d.unmute}>
            {soundOn ? '🔊' : '🔇'}
          </button>
        )}
      </div>
      <div className="demo-steps" aria-live="polite">
        <ol aria-hidden="true">
          {d.steps.map((s0, i) => (
            <li key={s0} className={phase === 'idle' ? '' : i + 1 < step ? 'done' : i + 1 === step ? 'now' : ''} />
          ))}
        </ol>
        {(running || phase === 'frozen' || phase === 'ended') && (
          <p key={step}>
            <b>{step}</b>
            {d.steps[step - 1]}
          </p>
        )}
      </div>
    </div>
  );
}
